import React, { useState } from "react";
import { Box, Paper, TextField, Checkbox, FormControlLabel, IconButton, Typography } from "@material-ui/core";
import { DragIndicator, DeleteOutline } from "@material-ui/icons";
import { isLabelOnlyField } from "../../constants/webFormFields";

/**
 * Lista reordenable de campos — drag-and-drop NATIVO HTML5, sin librería
 * nueva (doc 0417 §8: no es un grafo de nodos, `react-flow` es la
 * herramienta equivocada para esto). Sin precedente en el resto del
 * frontend (el único drag-and-drop existente es para soltar ARCHIVOS, no
 * para reordenar una lista) — implementado desde cero para este caso.
 */
const FieldListEditor = ({ fields, onUpdate, onRemove, onReorder }) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    // Firefox exige que se escriba algo en dataTransfer para permitir el arrastre.
    e.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (index !== overIndex) setOverIndex(index);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      onReorder(draggedIndex, index);
    }
    setDraggedIndex(null);
    setOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setOverIndex(null);
  };

  if (fields.length === 0) {
    return (
      <Box p={3} textAlign="center">
        <Typography color="textSecondary">
          Sin campos todavía. Agrega uno desde la paleta de la izquierda.
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gridGap={8}>
      {fields.map((field, index) => (
        <Paper
          key={`${field.tipo}-${index}`}
          draggable
          onDragStart={e => handleDragStart(e, index)}
          onDragOver={e => handleDragOver(e, index)}
          onDrop={e => handleDrop(e, index)}
          onDragEnd={handleDragEnd}
          style={{
            display: "flex",
            alignItems: "center",
            padding: 8,
            gap: 8,
            opacity: draggedIndex === index ? 0.4 : 1,
            border: overIndex === index && draggedIndex !== index ? "2px dashed #6366F1" : "2px solid transparent",
            cursor: "grab"
          }}
        >
          <DragIndicator style={{ color: "#999" }} />
          <Box flex={1}>
            <Typography variant="caption" color="textSecondary">
              {field.tipo}
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={field.etiqueta || ""}
              onChange={e => onUpdate(index, { etiqueta: e.target.value })}
              placeholder={
                isLabelOnlyField(field.tipo) ? "Texto a mostrar (título, aviso, etc.)" : "Etiqueta visible para el visitante"
              }
            />
          </Box>
          {/* Un texto decorativo no tiene caja de entrada — "Obligatorio" no
              tiene sentido para él. */}
          {!isLabelOnlyField(field.tipo) && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!field.obligatorio}
                  onChange={e => onUpdate(index, { obligatorio: e.target.checked })}
                  size="small"
                />
              }
              label="Obligatorio"
            />
          )}
          <IconButton size="small" onClick={() => onRemove(index)}>
            <DeleteOutline />
          </IconButton>
        </Paper>
      ))}
    </Box>
  );
};

export default FieldListEditor;
